import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ContentItem } from '../../utils/dummyData';
import { X, Download, Save, Share2, Trash2, Edit } from 'lucide-react';

interface PreviewModalProps {
  item: ContentItem | null;
  onClose: () => void;
}

const backdropVariants = {
  visible: { opacity: 1 },
  hidden: { opacity: 0 },
};

const modalVariants = {
  visible: { opacity: 1, scale: 1, transition: { duration: 0.3 } },
  hidden: { opacity: 0, scale: 0.9, transition: { duration: 0.3 } },
};

const PreviewModal: React.FC<PreviewModalProps> = ({ item, onClose }) => {
  if (!item) return null;

  return (
    <AnimatePresence>
      {item && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          onClick={onClose}
        >
          <motion.div
            className="bg-gray-900 rounded-lg shadow-xl w-full max-w-4xl h-full max-h-[90vh] flex flex-col"
            variants={modalVariants}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-700">
              <h2 className="text-xl font-bold">{item.title}</h2>
              <button onClick={onClose} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-grow p-4 overflow-y-auto">
              <div className="flex flex-col md:flex-row gap-8">
                {/* Preview */}
                <div className="w-full md:w-2/3">
                  {item.type === 'Image' && <img src={item.fullUrl} alt={item.title} className="rounded-lg w-full" />}
                  {item.type === 'Video' && <video src={item.fullUrl} controls className="rounded-lg w-full" />}
                  {item.type === 'Text' && <div className="bg-gray-800 p-4 rounded-lg whitespace-pre-wrap">{item.fullUrl}</div>}
                </div>
                {/* Metadata & Actions */}
                <div className="w-full md:w-1/3">
                  <div className="flex items-center gap-4 mb-4">
                    <img src={item.creator.avatarUrl} alt={item.creator.name} className="w-12 h-12 rounded-full" />
                    <div>
                        <h3 className="font-bold text-lg">{item.creator.name}</h3>
                        <p className="text-sm text-gray-400">@{item.creator.name.toLowerCase()}</p>
                    </div>
                  </div>
                  <h3 className="font-bold text-lg mb-2">Details</h3>
                  <div className="space-y-2 text-gray-300">
                    {item.prompt && (
                        <div>
                            <p className="font-semibold">Prompt:</p>
                            <p className="text-sm bg-gray-800 p-2 rounded">{item.prompt}</p>
                        </div>
                    )}
                    <p><span className="font-semibold">Date Created:</span> {new Date(item.date).toLocaleString()}</p>
                    <p><span className="font-semibold">Type:</span> {item.type}</p>
                    <p><span className="font-semibold">Category:</span> {item.category}</p>
                    {item.resolution && <p><span className="font-semibold">Resolution:</span> {item.resolution}</p>}
                    <p><span className="font-semibold">Likes:</span> {item.likes}</p>
                  </div>
                  <h3 className="font-bold text-lg mt-6 mb-2">Actions</h3>
                  <div className="flex flex-wrap gap-2">
                    <button className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-lg">
                        <Download size={18}/> Download
                    </button>
                    <button className="flex items-center gap-2 bg-gray-700 hover:bg-gray-600 text-white py-2 px-4 rounded-lg">
                        <Save size={18}/> Save
                    </button>
                    <button className="flex items-center gap-2 bg-gray-700 hover:bg-gray-600 text-white py-2 px-4 rounded-lg">
                        <Share2 size={18}/> Share
                    </button>
                    <button className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg">
                        <Trash2 size={18}/> Delete
                    </button>
                    <button className="flex items-center gap-2 bg-gray-700 hover:bg-gray-600 text-white py-2 px-4 rounded-lg">
                        <Edit size={18}/> Regenerate
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PreviewModal;
